import React, { useState, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useCurrentSizePage } from '@open-react-hooks/core';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';

import DialogMessage from 'components/DialogMessage';
import ModalConfirm from 'components/ModalConfirm';

import { StepperStart } from 'components/MenuMakeImport/Stepper/StepperStart';
import { StepperStartText } from 'components/MenuMakeImport/Stepper/StepperStartText';
import { StepperStartTitle } from 'components/MenuMakeImport/Stepper/StepperStartTitle';
import { StepperStartContentDialog } from 'components/MenuMakeImport/Stepper/StepperStartContentDialog';
import { StepperDialogEndContent } from 'components/MenuMakeImport/StepperDialogEndContent';

import useStyles from 'components/MenuMakeImport/styles';
import { UploadStep } from 'components/MenuMakeImport/UploadStep';
import { SuccessStep } from 'components/MenuMakeImport/SuccessStep';
import { useCreateMenu, useImportMenuModal } from 'components/MenuMakeImport/hooks';
import { SummaryDialog } from 'components/MenuMakeImport/SummaryDialog';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const MenuMakerImport = ({ onSuccess }) => {
  const { handleClose: onClose, importMenuModalIsOpen } = useImportMenuModal();
  const classes = useStyles();
  const theme = useTheme();

  const maxStepsRef = 2;

  const { t } = useTranslation();
  const [pageHeight] = useCurrentSizePage();
  const [activeStep, setActiveStep] = useState(1);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [isOpenSummayDialog, setIsOpenSummayDialog] = useState(false);

  const isMobileVersion = useMediaQuery(theme.breakpoints.down('md'));

  const excelFileRef = useRef(null);

  const {
    mutate: mutateCreateMenu,
    isLoading: isLoadingCreateMenu,
    isError: isErrorCreateMenu,
    isSuccess: isSuccessCreateMenu,
  } = useCreateMenu();

  const handleBack = async () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCloseStepper = () => {
    setOpenConfirmationModal(true);
  };

  const handleAcceptExitConfirmation = () => {
    onClose();
  };

  const handleCancelExitConfirmation = () => {
    setOpenConfirmationModal(false);
  };

  const STEP_CONTENT = {
    1: {
      title: t('menuMaker:importMenu.steps.1.title'),
      text: t('menuMaker:importMenu.steps.1.description'),
    },
    2: {
      title: t('menuMaker:importMenu.steps.2.title'),
      text: t('menuMaker:importMenu.steps.2.description'),
    },
  };

  const handleUploadExcelSuccess = (excelFile) => {
    excelFileRef.current = excelFile;
    setActiveStep(2);
  };

  const handleSubmitMenu = async (data) => {
    const formData = new FormData();
    formData.append('importedFile', excelFileRef.current);
    formData.append('menuData', JSON.stringify(data));
    mutateCreateMenu(
      {
        body: formData,
      },
      {
        onSuccess: () => {
          if (onSuccess) {
            onSuccess();
          }
        },
      },
    );
  };

  const handleCloseAllDialogs = () => {
    onClose();
  };

  const handleOpenSummayDialog = () => {
    setIsOpenSummayDialog(true);
  };

  if (!importMenuModalIsOpen) return null;

  return (
    <>
      <Dialog TransitionComponent={Transition} fullScreen open={importMenuModalIsOpen}>
        <DialogMessage
          onClose={handleCloseAllDialogs}
          open={isErrorCreateMenu}
          textDescription={t('menuMaker:importMenu.messages.errorDescription')}
          textTitle={t('menuMaker:importMenu.messages.error')}
          type="error"
        />
        <DialogMessage
          open={isLoadingCreateMenu}
          textDescription={t('menuMaker:importMenu.messages.loadingDescription')}
          textTitle={t('menuMaker:importMenu.messages.loading')}
          type="loading"
        />
        <DialogMessage
          onClose={handleOpenSummayDialog}
          open={isSuccessCreateMenu && !isOpenSummayDialog}
          textDescription={t('menuMaker:importMenu.messages.successDescription')}
          textTitle={t('menuMaker:importMenu.messages.success')}
          type="success"
        />
        <Box className={classes.rootStepper}>
          <StepperStartContentDialog
            activeStep={activeStep}
            isMobileVersion={isMobileVersion}
            loading
            onBack={handleBack}
            onClose={handleCloseStepper}
          >
            <StepperStart activeStep={activeStep} steps={maxStepsRef}>
              <StepperStartTitle>{STEP_CONTENT[activeStep].title}</StepperStartTitle>
              <StepperStartText>{STEP_CONTENT[activeStep].text}</StepperStartText>
            </StepperStart>
          </StepperStartContentDialog>

          <StepperDialogEndContent
            activeScroll={pageHeight <= 600}
            activeStep={activeStep}
            isMobileVersion={isMobileVersion}
            onClose={handleCloseStepper}
            steps={maxStepsRef}
          >
            {activeStep === 1 && <UploadStep onSuccess={handleUploadExcelSuccess} />}
            {activeStep === 2 && <SuccessStep onSubmit={handleSubmitMenu} />}
          </StepperDialogEndContent>
        </Box>
      </Dialog>
      <SummaryDialog onClose={handleCloseAllDialogs} open={isOpenSummayDialog} />

      {openConfirmationModal && (
        <ModalConfirm
          labelAccept={t('common:buttons.accept')}
          labelCancel={t('common:buttons.cancel')}
          message={t('menuMaker:messages.exitNoSaveMessage')}
          onAccept={handleAcceptExitConfirmation}
          onCancel={handleCancelExitConfirmation}
          open={openConfirmationModal}
          style={{ 'z-index': '1500 !important' }}
          title={t('menuMaker:messages.exitNoSaveTitle')}
        />
      )}
    </>
  );
};
